import { useState } from "react";
import { useAuth } from "@/lib/AuthContext";
import { updateGuild } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Image as ImageIcon, Upload, X } from "lucide-react";
import { toast } from "sonner";

export default function GuildSettingsDialog({ open, onOpenChange, guild, onSaved }) {
  const { refreshGuilds } = useAuth();
  const [name, setName] = useState(guild?.name || "");
  const [avatarUrl, setAvatarUrl] = useState(guild?.avatar_url || "");
  const [description, setDescription] = useState(guild?.description || "");
  const [teams, setTeams] = useState(Array.isArray(guild?.teams) && guild.teams.length > 0 ? guild.teams : ["Monday", "Thursday"]);
  const [newTeam, setNewTeam] = useState("");
  const [busy, setBusy] = useState(false);

  const addTeam = () => {
    const t = newTeam.trim().slice(0, 30);
    if (!t) return;
    if (teams.includes(t)) { toast.error("Team already exists"); return; }
    if (teams.length >= 20) { toast.error("Max 20 teams"); return; }
    setTeams([...teams, t]);
    setNewTeam("");
  };

  const removeTeam = (t) => {
    if (teams.length <= 1) { toast.error("At least one team required"); return; }
    setTeams(teams.filter((x) => x !== t));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be under 2 MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      // Load the raw image, then center-crop to a square + downscale to 256×256
      // so the avatar always renders correctly regardless of source aspect ratio.
      const img = new Image();
      img.onload = () => {
        const SIZE = 256;
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        const sx = Math.floor((img.naturalWidth - side) / 2);
        const sy = Math.floor((img.naturalHeight - side) / 2);
        const canvas = document.createElement("canvas");
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, sx, sy, side, side, 0, 0, SIZE, SIZE);
        // PNG keeps transparency for guild logos; jpeg would be smaller but lose alpha.
        // 256×256 PNG is usually 30–80 KB which fits comfortably.
        const dataUrl = canvas.toDataURL("image/png");
        setAvatarUrl(dataUrl);
      };
      img.onerror = () => toast.error("Could not read that image");
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setBusy(true);
    try {
      await updateGuild(guild.id, { name: name.trim(), avatar_url: avatarUrl, description: description.trim(), teams });
      await refreshGuilds();
      toast.success("Guild updated");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-background" data-testid="guild-settings-dialog">
        <DialogHeader>
          <DialogTitle className="heading text-base uppercase tracking-tight">Guild Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Guild Name</label>
            <Input
              data-testid="guild-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={60}
              className="bg-card/50 font-mono"
            />
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Avatar</label>
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-sm bg-card/40 border border-border/40 flex items-center justify-center overflow-hidden flex-shrink-0">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover object-center" />
                ) : (
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <Input
                  data-testid="guild-avatar-url"
                  value={avatarUrl.startsWith("data:") ? "[uploaded image]" : avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="Paste image URL or upload below"
                  className="bg-card/50 font-mono text-xs"
                  disabled={avatarUrl.startsWith("data:")}
                />
                <div className="flex gap-2">
                  <label className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-card/40 hover:bg-card/60 border border-border/40 text-xs cursor-pointer transition-colors">
                    <Upload className="h-3 w-3" /> Upload
                    <input data-testid="guild-avatar-file" type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                  </label>
                  {avatarUrl && (
                    <button data-testid="guild-avatar-clear" onClick={() => setAvatarUrl("")} className="px-2 py-1 rounded-sm text-xs text-muted-foreground hover:text-destructive transition-colors">
                      Remove
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground/70">Up to 2 MB. We auto-crop to a centered square — any aspect ratio works.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Description (optional)</label>
            <Textarea
              data-testid="guild-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={300}
              rows={3}
              placeholder="A short tagline that shows on your guild dashboard…"
              className="bg-card/50 text-sm"
            />
            <p className="text-[10px] text-muted-foreground/70 mt-1">{description.length}/300</p>
          </div>

          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Teams</label>
            <p className="text-[10px] text-muted-foreground/70 mb-2">
              Defines what shows in the team selector and the per-team stat cards. Common examples: "Monday", "Thursday", "Main", "Alt", "Progression", "Farm".
            </p>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="teams-list">
              {teams.map((t) => (
                <span key={t} data-testid={`team-chip-${t}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-card/40 border border-border/40 text-xs font-mono">
                  {t}
                  <button data-testid={`team-remove-${t}`} onClick={() => removeTeam(t)} className="text-muted-foreground hover:text-destructive ml-0.5">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="team-add-input"
                value={newTeam}
                onChange={(e) => setNewTeam(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTeam(); } }}
                placeholder="Add team name… (e.g. Tuesday, Main Raid)"
                maxLength={30}
                className="bg-card/50 font-mono text-xs"
              />
              <Button data-testid="team-add-btn" onClick={addTeam} disabled={!newTeam.trim()} size="sm"
                className="text-xs rounded-sm">
                Add
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground/70 mt-1">{teams.length} team(s)</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <DialogClose asChild>
            <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
          </DialogClose>
          <Button data-testid="guild-save-btn" onClick={handleSave} disabled={busy || name.trim().length < 3}
            size="sm" className="text-xs rounded-sm bg-purple-500 hover:bg-purple-600 text-white">
            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
